<template lang="pug">
teleport(to="body")
  .collection-code-sheet-backdrop(
    v-if="modelValue"
    role="dialog"
    aria-modal="true"
    aria-labelledby="collection-code-sheet-title"
    @click="handleBackdropClick"
  )
    .collection-code-sheet(@click.stop)
      .handle-bar

      .sheet-header
        .sticker-preview(v-if="sticker.url")
          img(:src="sticker.url" :alt="sticker.title")
        .sticker-preview.sticker-preview--placeholder(v-else)
          span {{ stickerInitial }}
        .header-text
          .pill-row
            span.collection-pill Collection
          p#collection-code-sheet-title.sticker-title {{ sticker.title }}
          p.collection-subtitle(v-if="collectionTitle") {{ collectionTitle }}

      .sheet-divider

      .celebration-block(v-if="showCelebration")
        p.celebration-title You completed {{ lastResult?.collectionTitle }}!
        p.celebration-subtitle Collection complete — nice work.

      template(v-else)
        .already-collected-banner(v-if="isAlreadyCollected")
          .banner-icon
            img(src="@/assets/icons/check.svg" alt="")
          div
            p.banner-title Already collected
            p.banner-subtitle This sticker is in your collection.

        p.helper-text(v-if="isAlreadyCollected")
          | Collect another? Enter a new code below to add another copy to your sticker wall.
        p.helper-text(v-else)
          | Enter the special code you received to add this sticker to your collection.

        label.field-label(for="collection-code-input") {{ isAlreadyCollected ? 'Enter Code' : 'Your Code' }}
        input#collection-code-input.code-input(
          ref="codeInputRef"
          v-model="codeInput"
          type="text"
          autocomplete="off"
          maxlength="32"
          placeholder="· · · · · · · ·"
          :class="inputClasses"
          :disabled="submitting || codeSuccess"
          @input="onCodeInput"
          @keydown.enter.prevent="handleSubmit"
        )

        .status-row.is-error(v-if="codeError")
          svg(
            width="14" height="14" fill="none" stroke="#ef4444"
            stroke-width="2" stroke-linecap="round" viewBox="0 0 24 24"
          )
            circle(cx="12" cy="12" r="10")
            path(d="M12 8v4m0 4h.01")
          p.error-text {{ codeError }}

        .status-row(v-else-if="codeSuccess")
          svg(
            width="14" height="14" fill="none" stroke="#0d9488" stroke-width="2.5"
            stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24"
          )
            path(d="M20 6L9 17l-5-5")
          p.success-text Code accepted! Adding to your collection…

        .status-spacer(v-else)

        .consent-block(v-if="showMarketingConsent && !codeSuccess")
          MarketingConsentCheckbox(
            ref="consentCheckboxRef"
            :organization-name="organizationName"
            :organization-id="organizationId"
          )

        button.submit-btn(
          type="button"
          :disabled="!canSubmit"
          @click="handleSubmit"
        )
          span(v-if="codeSuccess") Added ✓
          span(v-else-if="submitting") Adding…
          span(v-else) Add to Sticker Wall

        button.view-achievements-btn(
          v-if="isAlreadyCollected && !codeSuccess"
          type="button"
          @click="emitViewAchievements"
        ) View Achievements →

        button.cancel-btn(
          v-if="!isAlreadyCollected"
          type="button"
          @click="closeSheet"
        ) Cancel
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  nextTick,
  onUnmounted,
  ref,
  watch,
} from 'vue';
import { useStore } from 'vuex';
import MarketingConsentCheckbox from '@/components/MarketingConsentCheckbox.vue';
import { useStickerCollectionRedeem } from '@/composables/useStickerCollectionRedeem';
import { makeRequest } from '@/graphs/index';
import { giveConsent } from '@/graphs/gql';
import type { RedeemStickerCollectionCodeResult } from '@/data/stickerCollection.types';

export default defineComponent({
  name: 'StickerCollectionCodeSheet',
  components: { MarketingConsentCheckbox },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    sticker: {
      type: Object as () => { id: string; title: string; url?: string; slug?: string },
      required: true,
    },
    collectionTitle: {
      type: String,
      default: '',
    },
    alreadyCollected: {
      type: Boolean,
      default: false,
    },
    showMarketingConsent: {
      type: Boolean,
      default: false,
    },
    organizationId: {
      type: String,
      default: null,
    },
    organizationName: {
      type: String,
      default: 'the organization',
    },
  },
  emits: ['update:modelValue', 'success', 'view-achievements'],
  setup(props, { emit }) {
    const store = useStore();
    const { redeemCode, loading: redeemLoading } = useStickerCollectionRedeem();

    const codeInput = ref('');
    const codeError = ref('');
    const codeSuccess = ref(false);
    const showCelebration = ref(false);
    const lastResult = ref<RedeemStickerCollectionCodeResult | null>(null);
    const codeInputRef = ref<HTMLInputElement | null>(null);
    const consentCheckboxRef = ref<InstanceType<typeof MarketingConsentCheckbox> | null>(null);
    const requestGeneration = ref(0);

    const submitting = computed(() => redeemLoading.value);

    // The parent flips `alreadyCollected` as soon as a redeem succeeds; keep the sheet's
    // original variant until it closes so it does not flash "Already collected" mid-success.
    const frozenAlreadyCollected = ref<boolean | null>(null);
    const isAlreadyCollected = computed(
      () => frozenAlreadyCollected.value ?? props.alreadyCollected,
    );

    const stickerInitial = computed(() => {
      const t = props.sticker.title?.trim();
      return t ? t.charAt(0).toUpperCase() : '?';
    });

    const canSubmit = computed(
      () => codeInput.value.trim().length > 0
        && !submitting.value
        && !codeSuccess.value,
    );

    const inputClasses = computed(() => ({
      'is-error': Boolean(codeError.value),
      'is-success': codeSuccess.value,
    }));

    function resetForm() {
      codeInput.value = '';
      codeError.value = '';
      codeSuccess.value = false;
      frozenAlreadyCollected.value = null;
      showCelebration.value = false;
      lastResult.value = null;
    }

    function closeSheet() {
      emit('update:modelValue', false);
      resetForm();
    }

    function handleBackdropClick(event: MouseEvent) {
      if (event.target === event.currentTarget) {
        closeSheet();
      }
    }

    function emitViewAchievements() {
      emit('view-achievements');
    }

    async function submitConsentIfChecked() {
      if (!props.showMarketingConsent || !props.organizationId || !consentCheckboxRef.value) {
        return;
      }
      const isConsentChecked = consentCheckboxRef.value.getConsentValue();
      if (!isConsentChecked) {
        return;
      }
      try {
        const token = store.getters['users/getToken'];
        await makeRequest(
          'mutation',
          giveConsent,
          { orgId: props.organizationId },
          { token },
        );
      } catch (err) {
        console.error('Consent after redeem failed:', err);
      }
    }

    async function handleSubmit() {
      if (!canSubmit.value) {
        return;
      }

      codeError.value = '';
      const generation = requestGeneration.value + 1;
      requestGeneration.value = generation;

      const outcome = await redeemCode({
        stickerId: props.sticker.id,
        code: codeInput.value,
        // Redeemed for whoever the user is acting as (themselves or an organization)
        orgId: store.getters['actingEntity/orgId'] ?? null,
      });

      // The sheet was closed/reopened mid-request: a failure is stale, but a redeem that went
      // through must still reach the page so the wall and progress refresh.
      if (!outcome.ok) {
        if (generation === requestGeneration.value) {
          codeError.value = outcome.error;
        }
        return;
      }

      await submitConsentIfChecked();

      if (generation !== requestGeneration.value) {
        emit('success', outcome.data);
        return;
      }

      lastResult.value = outcome.data;
      frozenAlreadyCollected.value = props.alreadyCollected;
      codeSuccess.value = true;

      if (outcome.data.justCompleted) {
        showCelebration.value = true;
      }

      emit('success', outcome.data);

      window.setTimeout(() => {
        if (generation !== requestGeneration.value) {
          return;
        }
        closeSheet();
      }, outcome.data.justCompleted ? 2200 : 1200);
    }

    let previousBodyOverflow = '';
    let bodyLocked = false;

    function onDocumentKeydown(event: KeyboardEvent) {
      if (event.key === 'Escape' && props.modelValue) {
        event.preventDefault();
        closeSheet();
      }
    }

    function lockBodyScroll() {
      if (bodyLocked) return;
      bodyLocked = true;
      previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }

    function unlockBodyScroll() {
      if (!bodyLocked) return;
      bodyLocked = false;
      document.body.style.overflow = previousBodyOverflow;
    }

    watch(
      () => props.modelValue,
      async (open) => {
        if (open) {
          requestGeneration.value += 1;
          resetForm();
          lockBodyScroll();
          document.addEventListener('keydown', onDocumentKeydown);
          await nextTick();
          codeInputRef.value?.focus();
        } else {
          document.removeEventListener('keydown', onDocumentKeydown);
          unlockBodyScroll();
        }
      },
    );

    onUnmounted(() => {
      document.removeEventListener('keydown', onDocumentKeydown);
      unlockBodyScroll();
    });

    function onCodeInput() {
      codeInput.value = codeInput.value.toUpperCase();
      if (codeError.value) {
        codeError.value = '';
      }
    }

    return {
      codeInput,
      codeError,
      codeSuccess,
      showCelebration,
      lastResult,
      codeInputRef,
      consentCheckboxRef,
      submitting,
      stickerInitial,
      canSubmit,
      isAlreadyCollected,
      inputClasses,
      handleBackdropClick,
      handleSubmit,
      closeSheet,
      emitViewAchievements,
      onCodeInput,
    };
  },
});
</script>

<style lang="scss" scoped>
.collection-code-sheet-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
  animation: collection-sheet-fade 0.25s ease;
}

.collection-code-sheet {
  width: 100%;
  max-width: 32rem;
  max-height: 90vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 1.5rem 1.5rem 0 0;
  padding: 1.25rem 1.5rem 2.5rem;
  animation: collection-sheet-slide 0.3s ease;
}

.handle-bar {
  width: 2.5rem;
  height: 0.25rem;
  background: #e5e7eb;
  border-radius: 999px;
  margin: 0 auto 1.25rem;
}

.sheet-header {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.sticker-preview {
  width: 4rem;
  height: 4rem;
  flex-shrink: 0;
  border-radius: 1rem;
  overflow: hidden;
  background: #f3f4f6;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &--placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    font-weight: 800;
    color: #9ca3af;
  }
}

.header-text {
  flex: 1;
  min-width: 0;
}

.collection-pill {
  display: inline-block;
  background: #0d9488;
  color: #fff;
  font-size: 0.625rem;
  font-weight: 900;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 0.125rem 0.5rem;
  border-radius: 0.25rem;
}

.sticker-title {
  font-size: 1rem;
  font-weight: 900;
  color: #1a2744;
  margin: 0.125rem 0 0;
  line-height: 1.25;
}

.collection-subtitle {
  font-size: 0.75rem;
  font-weight: 600;
  color: #9ca3af;
  margin: 0.125rem 0 0;
}

.sheet-divider {
  height: 1px;
  background: #f3f4f6;
  margin-bottom: 1.25rem;
}

.helper-text {
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.625;
  margin: 0 0 1.25rem;
}

.already-collected-banner {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  background: #e6f9f9;
  border-radius: 1rem;
  padding: 0.875rem 1rem;
  margin-bottom: 1.25rem;
}

.banner-icon {
  width: 2rem;
  height: 2rem;
  flex-shrink: 0;
  background: #0d9488;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 0.875rem;
    height: 0.875rem;
    filter: brightness(0) invert(1);
  }
}

.banner-title {
  font-size: 0.875rem;
  font-weight: 900;
  color: #1a2744;
  margin: 0;
}

.banner-subtitle {
  font-size: 0.75rem;
  font-weight: 600;
  color: #9ca3af;
  margin: 0.15rem 0 0;
}

.field-label {
  display: block;
  font-size: 0.625rem;
  font-weight: 900;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #9ca3af;
  margin-bottom: 0.5rem;
}

.code-input {
  width: 100%;
  border: 2px solid #e5e7eb;
  border-radius: 1rem;
  padding: 1rem;
  font-size: 1.25rem;
  font-weight: 900;
  text-align: center;
  letter-spacing: 0.4em;
  text-transform: uppercase;
  color: #1a2744;
  background: #f9fafb;
  margin-bottom: 0.5rem;
  outline: none;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:focus {
    border-color: #0d9488;
    background: #fff;
  }

  &.is-error {
    border-color: #fca5a5;
    background: #fef2f2;
    color: #dc2626;
  }

  &.is-success {
    border-color: #0d9488;
    background: #e6f9f9;
    color: #0d9488;
  }
}

.status-spacer {
  height: 1rem;
}

.status-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;

  svg {
    flex-shrink: 0;
  }
}

.error-text {
  font-size: 0.75rem;
  font-weight: 600;
  color: #ef4444;
  margin: 0;
}

.success-text {
  font-size: 0.75rem;
  font-weight: 700;
  color: #0d9488;
  margin: 0;
}

.consent-block {
  margin: 0.75rem 0 1rem;
}

.submit-btn {
  width: 100%;
  border: none;
  border-radius: 999px;
  padding: 1rem;
  font-size: 1rem;
  font-weight: 700;
  background: #0d9488;
  color: #fff;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.1s ease;

  &:hover:not(:disabled) {
    background: #0d9488;
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    background: #f3f4f6;
    color: #d1d5db;
    cursor: not-allowed;
  }
}

.view-achievements-btn {
  width: 100%;
  margin-top: 0.35rem;
  border: none;
  background: transparent;
  color: #0d9488;
  font-size: 0.875rem;
  font-weight: 700;
  padding: 0.75rem;
  cursor: pointer;
}

.cancel-btn {
  width: 100%;
  margin-top: 0.35rem;
  border: none;
  background: transparent;
  color: #9ca3af;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.75rem;
  cursor: pointer;
}

.celebration-block {
  text-align: center;
  padding: 1.5rem 0 2rem;
}

.celebration-title {
  font-size: 1.125rem;
  font-weight: 800;
  color: #1a2744;
  margin: 0 0 0.5rem;
}

.celebration-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

@keyframes collection-sheet-fade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes collection-sheet-slide {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}
</style>
